/**
 * Local display surfaces — a monitor on *this* machine, driven straight from
 * the tab that owns the canvas.
 *
 * The window we open is same-origin, so the owning tab can paint into it: no
 * signaling room, no peer connection, no encoder. One JS context keeps the
 * canvas, the bus, and the hardware connections; each surface is only pixels.
 * Network receivers (other machines, Pis, phones) still go through
 * `screens.cast()`.
 *
 * The blit loop is scheduled by the *surface* window, so a display keeps
 * animating at its own refresh rate. The owning tab still has to be visible
 * for its own render loop to run at full rate.
 */

export interface ScreenPlacement {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface LocalSurfaceOptions {
  /** Window title and slate name. */
  label?: string;
  /** Reused browsing-context name. Omit for a fresh, non-colliding window. */
  windowName?: string;
  /** Applied as `window.open` features when known before the gesture. */
  placement?: ScreenPlacement;
  /** Frame cap. Omit to follow the surface display's refresh rate. */
  fps?: number;
  /** Fired once when the visitor closes the window. */
  onClose?: () => void;
}

export interface LocalSurface {
  readonly kind: "local";
  readonly window: Window;
  readonly closed: boolean;
  /** Move/resize after the fact, for placements resolved post-gesture. */
  place(placement: ScreenPlacement): void;
  close(): Promise<void>;
}

/** Popup blocked — falling back to a network transport would fail the same way. */
export class SurfaceBlockedError extends Error {
  constructor() {
    super("The display window was blocked; allow popups for this page and try again");
    this.name = "SurfaceBlockedError";
  }
}

const CURSOR_IDLE_MS = 3000;

const SURFACE_CSS = `
  html, body { margin: 0; height: 100%; background: #000; overflow: hidden; }
  body { display: flex; align-items: center; justify-content: center; }
  body[data-gr-idle] { cursor: none; }
  canvas, video {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
    background: #000;
  }
`;

let surfaceSeq = 0;

/**
 * `window.open` treats the name as a browsing-context target, so a shared
 * default silently retargets the previous surface instead of opening a second
 * monitor. Callers that *want* reuse pass an explicit stable name.
 */
export function nextSurfaceWindowName(label?: string): string {
  surfaceSeq += 1;
  const slug = (label ?? "display")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `granrojo-surface-${slug || "display"}-${surfaceSeq}`;
}

export function surfaceWindowFeatures(placement?: ScreenPlacement): string {
  const features = ["popup=yes"];
  if (placement) {
    const left = Math.round(placement.left);
    const top = Math.round(placement.top);
    const width = Math.round(placement.width);
    const height = Math.round(placement.height);
    // left/top and screenX/screenY — Chrome's Window Management path
    // honours these when permission is granted; macOS ignores a later moveTo.
    features.push(
      `left=${left}`,
      `top=${top}`,
      `screenX=${left}`,
      `screenY=${top}`,
      `width=${width}`,
      `height=${height}`,
    );
  }
  return features.join(",");
}

/**
 * Frame gate for an optional cap. No cap means every animation frame draws,
 * which is what a dedicated display usually wants.
 */
export function createFrameGate(fps?: number): (now: number) => boolean {
  if (!fps || fps <= 0) return () => true;
  const interval = 1000 / fps;
  let last = Number.NEGATIVE_INFINITY;
  return (now) => {
    if (now - last < interval) return false;
    last = now;
    return true;
  };
}

export function openLocalSurface(
  source: HTMLCanvasElement | MediaStream,
  options: LocalSurfaceOptions = {},
): LocalSurface {
  const name = options.windowName ?? nextSurfaceWindowName(options.label);
  const win = window.open("about:blank", name, surfaceWindowFeatures(options.placement));
  if (!win) throw new SurfaceBlockedError();

  try {
    return attachSurface(win, source, options);
  } catch (error) {
    win.close();
    throw error;
  }
}

function attachSurface(
  win: Window,
  source: HTMLCanvasElement | MediaStream,
  options: LocalSurfaceOptions,
): LocalSurface {
  const doc = win.document;
  const label = options.label ?? "Granrojo display";
  doc.title = label;

  const style = doc.createElement("style");
  style.textContent = SURFACE_CSS;
  doc.head?.replaceChildren(style);

  // Branch on the source, never on the view: elements created in the surface
  // document belong to its realm, so `instanceof` from this realm is false.
  const stream = source instanceof MediaStream ? source : null;
  const view = stream ? attachStreamView(doc, stream) : doc.createElement("canvas");
  doc.body.replaceChildren(view);

  const stopBlit = stream
    ? () => {}
    : startBlitLoop(win, view as HTMLCanvasElement, source as HTMLCanvasElement, options.fps);

  let cursorTimer = 0;
  const idle = (): void => doc.body.setAttribute("data-gr-idle", "");
  const onMove = (): void => {
    doc.body.removeAttribute("data-gr-idle");
    win.clearTimeout(cursorTimer);
    cursorTimer = win.setTimeout(idle, CURSOR_IDLE_MS);
  };
  const onKey = (event: KeyboardEvent): void => {
    if (event.key !== "f" && event.key !== "F") return;
    event.preventDefault();
    if (doc.fullscreenElement) void doc.exitFullscreen().catch(() => {});
    else void doc.documentElement.requestFullscreen().catch(() => {});
  };
  doc.addEventListener("mousemove", onMove, { passive: true });
  doc.addEventListener("keydown", onKey);
  cursorTimer = win.setTimeout(idle, CURSOR_IDLE_MS);

  let released = false;
  const release = (): void => {
    if (released) return;
    released = true;
    stopBlit();
    win.clearTimeout(cursorTimer);
    window.clearInterval(watch);
    doc.removeEventListener("mousemove", onMove);
    doc.removeEventListener("keydown", onKey);
    win.removeEventListener("pagehide", onGone);
  };

  let notified = false;
  const onGone = (): void => {
    release();
    if (notified) return;
    notified = true;
    options.onClose?.();
  };

  // `pagehide` covers the visitor closing the window; the poll covers the
  // cases where a closing window never delivers it.
  win.addEventListener("pagehide", onGone);
  const watch = window.setInterval(() => {
    if (win.closed) onGone();
  }, 1000);

  return {
    kind: "local",
    window: win,
    get closed() {
      return win.closed;
    },
    place(placement) {
      if (win.closed) return;
      const left = Math.round(placement.left);
      const top = Math.round(placement.top);
      const width = Math.round(placement.width);
      const height = Math.round(placement.height);
      win.moveTo(left, top);
      win.resizeTo(width, height);
      // macOS Chrome sometimes applies the first moveTo against the
      // current display. A second pass after layout usually sticks.
      win.requestAnimationFrame(() => {
        if (win.closed) return;
        win.moveTo(left, top);
        win.resizeTo(width, height);
      });
    },
    async close() {
      notified = true;
      release();
      win.close();
    },
  };
}

function attachStreamView(doc: Document, stream: MediaStream): HTMLVideoElement {
  const video = doc.createElement("video");
  video.autoplay = true;
  video.muted = true;
  video.playsInline = true;
  // Cross-realm handoff: if this document rejects the parent's MediaStream,
  // the caller falls back to a network transport.
  video.srcObject = stream;
  return video;
}

function startBlitLoop(
  win: Window,
  view: HTMLCanvasElement,
  source: HTMLCanvasElement,
  fps?: number,
): () => void {
  const ctx = view.getContext("2d");
  if (!ctx) throw new Error("The display window could not open a 2D context");
  const gate = createFrameGate(fps);

  // Backing store matches the source, so CSS `object-fit: contain` does the
  // letterboxing and the blit stays a 1:1 copy.
  const resize = (): void => {
    if (view.width === source.width && view.height === source.height) return;
    view.width = source.width;
    view.height = source.height;
  };
  resize();

  let frame = 0;
  let stopped = false;
  const draw = (now: number): void => {
    if (stopped || win.closed) return;
    frame = win.requestAnimationFrame(draw);
    if (!gate(now)) return;
    resize();
    ctx.drawImage(source, 0, 0);
  };
  frame = win.requestAnimationFrame(draw);

  return () => {
    stopped = true;
    if (frame) win.cancelAnimationFrame(frame);
  };
}
